import { z } from 'zod';
/**
 * Signal vocabulary — 4-state assumption / metric classification used by
 * Phase 6's Signal tab + Public Deal Link Hero Metrics + Assumption Integrity
 * cards.
 *
 * Per-metric classification rule (driven entirely from benchmark-data-seed
 * rows; NO hardcoded thresholds):
 * - `aligned`        — value ∈ [value_low, value_high]
 * - `aggressive`     — value > value_high
 * - `conservative`   — value < value_low
 * - `differentiated` — user explicitly tagged as deliberate thesis OR value
 *                       is >2x outside the band
 *
 * When benchmarks are not seeded for a metric, render the metric WITHOUT a
 * signal dot — no false-positive classification.
 *
 * Defined in Phase 1 to lock the cross-package shape early so Phase 2's
 * `<Chip>` primitive + Phase 6's Signal tab can both consume it.
 */
export const signalSchema = z.enum([
    'aligned',
    'aggressive',
    'conservative',
    'differentiated',
]);
/**
 * Signal variant — drives which Signal-tab body component (and which Public
 * Deal Link body) renders. Resolved from `model_registry.signal_variant`
 * (Phase 6 schema change). Null at the consumer disables the Signal tab
 * (admin has not assigned a variant for this model).
 *
 * Locked at conversation creation by the chosen model; switching variants
 * requires forking the conversation onto a different-variant model. There is
 * NO user-facing toggle.
 */
export const signalVariantSchema = z.enum(['operating-asset', 'new-development']);
/**
 * Visual dot-state for the per-metric / per-assumption signal indicator.
 *
 * Semantic alias of `signalSchema` — same 4-value vocabulary, named to match
 * the component prop convention (`<HeroMetricCell signalDot={...} />`). Lifted
 * from `apps/web/src/components/builder/signal/HeroMetricCell.tsx` web-local
 * by `deal-signal-overhaul` Phase 4 Session 1 (2026-05-11) so consumers
 * across the workspace (web `<HeroMetricCell>` / `<AssumptionCard>`,
 * `apps/web/src/lib/signal-classification.ts`, future agents-side telemetry)
 * share a single canonical export.
 */
export const signalDotStateSchema = signalSchema;
